import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { operations, type OperationInput } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { buttonClassName } from '@/shared/ui/buttonStyles';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DetailTabs } from '@/shared/ui/DetailLayout';
import { Icon } from '@/shared/ui/Icon';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton, SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { Stepper } from '@/shared/ui/Stepper';
import { useToast } from '@/shared/ui/useToast';

import { ChangesHistoryDialog } from './ChangesHistoryDialog';
import { EngineHistoryTab } from './EngineHistoryTab';
import { EsteiraDaOperacao } from './EsteiraDaOperacao';
import { FollowUpTab } from './FollowUpTab';
import { OperationDocumentsTab } from './OperationDocumentsTab';
import { OperationForm } from './OperationForm';
import { QuoteDeclineDialog } from './QuoteDeclineDialog';
import { BrokerTimeline } from './quote/BrokerTimeline';
import {
  IdentificacaoDaOperacao,
  MovimentoTravado,
  TelaDaEtapa,
  type IrParaQuadro,
} from './TelaDaEtapa';
import {
  dicaDaEtapaTravada,
  etapaDaEsteira,
  situacaoDaTela,
  telaAberta,
  telaDoQuadro,
} from './telasDaOperacao';

/** As abas do detalhe da cotacao do OnPoint, nesta ordem (COT-030 a COT-034). */
const abasDaCotacao = [
  { key: 'resumo', title: 'Resumo da cotacao' },
  { key: 'motor', title: 'Historico do motor' },
  { key: 'follow-up', title: 'Follow up' },
  { key: 'documentos', title: 'Documentos' },
] as const;

type AbaDaCotacao = (typeof abasDaCotacao)[number]['key'];

/**
 * `/operacoes/:operationId`, com a visao por perfil (P-NCT): a corretora ve a linha do
 * tempo da cotacao dela, sem os paineis de parecer da seguradora; a seguradora continua
 * com a Operacao 360.
 */
export function OperationDetailPage() {
  const { session } = useAuth();
  return session?.broker_scoped === true ? (
    <BrokerTimeline />
  ) : (
    <InsurerOperationDetail />
  );
}

/**
 * Operacao 360, que e o detalhe da cotacao do OnPoint.
 *
 * A tela reune, em um unico lugar, as partes, os valores, a posicao na esteira, os
 * proximos passos possiveis e o historico. O material legado espalhava esses dados
 * por telas diferentes, e a pergunta "em que pe esta esta operacao" exigia abrir
 * quatro delas.
 *
 * As abas sao as do OnPoint (Resumo da cotacao, Historico do motor, Follow up e
 * Documentos), com a aba aberta no endereco (?aba=). Os quadros que o SeguraCore ja
 * tinha continuam: pareceres, emissao, minuta, CCG e avalistas no Resumo; pendencias,
 * conversa e linha do tempo no Follow up.
 *
 * O Resumo tem uma tela por etapa da esteira (ADR 0107), com a etapa vista no endereco
 * (?etapa=): o indicador de etapas abre as telas concluidas e a atual, e cada tela
 * termina em Voltar e Continuar. A identificacao, o Proximo passo e a esteira ficam a
 * vista em todas.
 */
function InsurerOperationDetail() {
  const { operationId = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editando, setEditando] = useState(false);
  const [declinando, setDeclinando] = useState(false);
  const [vendoAlteracoes, setVendoAlteracoes] = useState(false);
  const [parametros, setParametros] = useSearchParams();
  const aba: AbaDaCotacao =
    abasDaCotacao.find((item) => item.key === parametros.get('aba'))?.key ?? 'resumo';

  function abrirAba(chave: AbaDaCotacao) {
    const proximos = new URLSearchParams(parametros);
    proximos.set('aba', chave);
    setParametros(proximos, { replace: true });
  }

  // A tela de cada etapa fica no endereco (?etapa=), como a aba: o link copiado e o
  // recarregar abrem a mesma tela. Abrir uma etapa de outra aba volta ao Resumo.
  function abrirEtapa(etapa: string) {
    const proximos = new URLSearchParams(parametros);
    proximos.set('etapa', etapa);
    if (aba !== 'resumo') proximos.set('aba', 'resumo');
    setParametros(proximos, { replace: true });
  }

  // Voltar e Continuar ficam no fim da tela: a tela seguinte abre do comeco.
  const topoDasEtapas = useRef<HTMLDivElement>(null);
  function abrirEtapaPeloRodape(etapa: string) {
    abrirEtapa(etapa);
    rolarAte(topoDasEtapas.current);
  }

  // O "Ir para ..." do movimento travado pode levar a outra tela: o quadro so existe
  // depois que ela monta e o parecer carrega, e a rolagem espera por ele.
  const [ancoraPedida, setAncoraPedida] = useState<string | null>(null);
  useEffect(() => {
    if (!ancoraPedida) return;
    let tentativas = 0;
    const espera = window.setInterval(() => {
      const quadro = document.getElementById(ancoraPedida);
      tentativas += 1;
      if (quadro || tentativas >= 40) {
        window.clearInterval(espera);
        rolarAte(quadro);
        setAncoraPedida(null);
      }
    }, 50);
    return () => window.clearInterval(espera);
  }, [ancoraPedida]);

  const consulta = useQuery({
    queryKey: ['operations', operationId],
    queryFn: ({ signal }) => operations.get(operationId, signal),
    enabled: operationId !== '',
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['operations'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const mover = useMutation({
    mutationFn: ({ stage, reason }: { stage: string; reason?: string }) =>
      operations.transition(operationId, stage, reason),
    onSuccess: (operacao) => {
      toast.success(
        `Operacao movida para ${statusLabel('operacao', operacao.stage).toLowerCase()}.`,
      );
      // Depois do movimento, a pagina mostra a tela da etapa nova: sem a etapa no
      // endereco, a tela aberta e a da etapa em que a esteira esta.
      setParametros(
        (atuais) => {
          const proximos = new URLSearchParams(atuais);
          proximos.delete('etapa');
          return proximos;
        },
        { replace: true },
      );
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const atualizar = useMutation({
    mutationFn: (input: OperationInput) => operations.update(operationId, input),
    onSuccess: () => {
      toast.success('Operacao atualizada.');
      setEditando(false);
      invalidar();
    },
  });

  if (consulta.isPending) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-8 w-64" />
        <Card>
          <CardBody>
            <SkeletonText lines={5} />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Operacao" />
        <Alert tone="danger" title="Nao foi possivel abrir a operacao">
          <div className="flex flex-col gap-3">
            <p>{consulta.error.message}</p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigate('/operacoes')}
            >
              Voltar para a lista
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  const operacao = consulta.data;
  const avancos = operacao.transitions.filter((item) => item.stage !== 'declinada');
  const declinio = operacao.transitions.find((item) => item.stage === 'declinada');
  const podeAtualizar = can('operacoes.operacao.update');
  const etapa = telaAberta(parametros.get('etapa'), operacao);
  const situacao = situacaoDaTela(etapa, operacao);

  const irParaQuadro: IrParaQuadro = (quadro, ancora) => {
    const tela = telaDoQuadro(quadro, etapaDaEsteira(operacao), operacao.progression);
    if (tela && (tela !== etapa || aba !== 'resumo')) abrirEtapa(tela);
    setAncoraPedida(ancora);
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={`Operacao ${operacao.number}`}
        description={operacao.subject}
        meta={
          <>
            <StatusBadge kind="operacao" value={operacao.stage} />
            {operacao.is_overdue ? (
              <Badge tone="danger" dot>
                Prazo vencido
              </Badge>
            ) : null}
            {/* Produto e modalidade sao categorias: a cor so separa uma da outra. */}
            <Badge tone="violet">{operacao.product.label}</Badge>
            <Badge tone="indigo">{operacao.modality.label}</Badge>
          </>
        }
        actions={
          <>
            {/* A hierarquia do cabecalho: navegacao em ghost, consulta em secondary e a
                edicao, a acao do cabecalho, em soft. O primario fica no Proximo passo. */}
            <Link to="/operacoes" className={buttonClassName({ variant: 'ghost' })}>
              Lista de cotacoes
            </Link>
            {/* Acompanhamento da cotacao (P-COT-3): a pagina da linha do tempo e o
                historico de alteracoes ("Ver alteracoes" do OnPoint). */}
            <Link
              to={`/operacoes/${operacao.id}/linha-do-tempo`}
              className={buttonClassName({ variant: 'ghost' })}
            >
              Linha do tempo
            </Link>
            <Button variant="secondary" onClick={() => setVendoAlteracoes(true)}>
              Ver alteracoes
            </Button>
            {podeAtualizar && !operacao.is_terminal ? (
              <Button variant="soft" onClick={() => setEditando(true)}>
                <Icon name="pencil" size={15} />
                Editar
              </Button>
            ) : null}
          </>
        }
      />

      {/* Cada etapa abre a sua tela no Resumo; a etapa adiante da atual fica travada.
          Na operacao declinada, a marca fica onde ela parou. */}
      <div ref={topoDasEtapas} className="scroll-mt-24">
        <Card>
          <CardBody>
            <Stepper
              label="Etapas da operacao"
              steps={operacao.progression.map((stage) => {
                const travada = situacaoDaTela(stage, operacao) === 'travada';
                return {
                  key: stage,
                  label: statusLabel('operacao', stage),
                  locked: travada,
                  disabledReason: travada ? dicaDaEtapaTravada : undefined,
                };
              })}
              currentKey={etapaDaEsteira(operacao)}
              terminated={operacao.stage === 'declinada'}
              onStepClick={abrirEtapa}
              selectedKey={aba === 'resumo' ? etapa : undefined}
            />
          </CardBody>
        </Card>
      </div>

      {operacao.stage === 'declinada' ? (
        <Alert tone="danger" title="Operacao declinada">
          {operacao.decline_reason}
        </Alert>
      ) : null}

      <DetailTabs
        label="Abas da cotacao"
        tabs={[...abasDaCotacao]}
        active={aba}
        onChange={abrirAba}
      />

      {aba === 'resumo' ? (
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="flex flex-col gap-5 lg:col-span-2">
            <IdentificacaoDaOperacao operacao={operacao} />

            {/* A chave pela etapa monta cada tela do zero: o que foi digitado e nao
                gravado numa tela nao vai junto para a outra. */}
            {situacao !== 'travada' ? (
              <TelaDaEtapa
                key={etapa}
                operacao={operacao}
                etapa={etapa}
                situacao={situacao}
                onAbrirEtapa={abrirEtapaPeloRodape}
                onIrPara={irParaQuadro}
                onMover={(stage) => mover.mutate({ stage })}
                movendo={mover.isPending}
              />
            ) : null}
          </div>

          <div className="flex flex-col gap-5">
            <Card>
              <CardHeader
                title="Proximo passo"
                description="Os movimentos possiveis vem do servidor."
              />
              <CardBody className="flex flex-col gap-2.5">
                {operacao.is_terminal ? (
                  <p className="text-sm text-ink-500">
                    A operacao chegou a uma etapa terminal e nao aceita novos
                    movimentos.
                  </p>
                ) : (
                  <>
                    {avancos.map((transicao) =>
                      transicao.blocked_reason ? (
                        <MovimentoTravado
                          key={transicao.stage}
                          transicao={transicao}
                          onIrPara={irParaQuadro}
                        />
                      ) : (
                        <Button
                          key={transicao.stage}
                          loading={mover.isPending}
                          onClick={() => mover.mutate({ stage: transicao.stage })}
                        >
                          {transicao.label}
                        </Button>
                      ),
                    )}

                    {declinio ? (
                      <Button
                        variant="danger"
                        disabled={mover.isPending}
                        onClick={() => setDeclinando(true)}
                      >
                        Declinar operacao
                      </Button>
                    ) : null}

                    {avancos.length === 0 && !declinio ? (
                      <p className="text-sm text-ink-500">
                        {operacao.stage === 'em_emissao'
                          ? 'O proximo passo e emitir a garantia, no quadro de emissao. A apolice e a comissao nascem junto com a mudanca de etapa.'
                          : 'Nenhum movimento disponivel para o seu acesso.'}
                      </p>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>

            <EsteiraDaOperacao operacao={operacao} />

            {/* As observacoes valem para a operacao inteira: ficam a vista em toda
                etapa, ao lado do Proximo passo. */}
            {operacao.notes ? (
              <Card>
                <CardHeader title="Observacoes" />
                <CardBody>
                  <p className="whitespace-pre-wrap text-sm text-ink-600">
                    {operacao.notes}
                  </p>
                </CardBody>
              </Card>
            ) : null}
          </div>
        </div>
      ) : null}

      {aba === 'motor' ? <EngineHistoryTab operationId={operacao.id} /> : null}

      {aba === 'follow-up' ? <FollowUpTab operacao={operacao} /> : null}

      {aba === 'documentos' ? <OperationDocumentsTab operacao={operacao} /> : null}

      <ChangesHistoryDialog
        operationId={operacao.id}
        open={vendoAlteracoes}
        onClose={() => setVendoAlteracoes(false)}
      />

      <Modal
        open={editando}
        onClose={() => setEditando(false)}
        title={`Editar operacao ${operacao.number}`}
        description="A etapa nao muda por aqui: ela tem caminho proprio."
        size="lg"
      >
        <OperationForm
          operation={operacao}
          submitting={atualizar.isPending}
          error={atualizar.error}
          onCancel={() => setEditando(false)}
          onSubmit={(input) => atualizar.mutate(input)}
        />
      </Modal>

      {/* O declinio e a negativa da cotacao (P-COT-3): a mesma transicao, com o
          motivo padronizado e a justificativa (SaveProposalDenialJustification). */}
      <QuoteDeclineDialog
        operationId={operacao.id}
        mode="negativa"
        open={declinando}
        onClose={() => setDeclinando(false)}
      />
    </div>
  );
}

/** Rola a pagina ate o elemento, quando ele existe e o navegador sabe rolar. */
function rolarAte(elemento: HTMLElement | null) {
  if (elemento && typeof elemento.scrollIntoView === 'function') {
    elemento.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
